import { useOldDatasetHref } from "@/features/datasets/useDatasetsAccess";
import { useRepositories } from "@/features/repos/hooks/useRepositories";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { formatDate } from "@/utils/format";
import { Link } from "react-router-dom";

const LINK =
  "rounded-sm transition-colors hover:text-zinc-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400";

const countLabel = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** Every repository of the user, newest first, with the datasets that each one backs. */
export const RepositoriesPage = () => {
  useDocumentTitle("Repositories");
  const { list, datasetsByRepo, isLoading, isError } = useRepositories();
  const datasetHref = useOldDatasetHref();

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="mx-auto max-w-4xl px-8 py-8">
        <header>
          <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900">
            Repositories
          </h1>
          <p className="mt-3 text-base leading-7 text-zinc-600">
            A repository keeps the papers of a dataset and the evidence for each
            value, with a history of every change. The agent can also make one
            in a chat.
          </p>
        </header>

        {isLoading ? (
          <p className="mt-8 text-sm text-zinc-500" aria-live="polite">
            Loading the repositories…
          </p>
        ) : isError ? (
          <p role="alert" className="mt-8 text-sm text-zinc-600">
            Could not load the repositories. Reload the page to try again.
          </p>
        ) : list.length === 0 ? (
          <p className="mt-8 font-serif text-base text-zinc-500">
            No repositories yet. Each dataset that you make in Datasets keeps
            its evidence in one.
          </p>
        ) : (
          <ul
            aria-label="All repositories"
            className="mt-8 divide-y divide-zinc-200 border-y border-zinc-200"
          >
            {list.map((repo) => {
              const datasets = datasetsByRepo.get(repo.id) ?? [];
              return (
                <li key={repo.id} className="py-4">
                  <Link
                    to={`/repos/${encodeURIComponent(repo.id)}`}
                    className={`${LINK} break-words text-base font-medium text-zinc-900`}
                  >
                    {repo.name}
                  </Link>
                  {repo.description && (
                    <p className="mt-1 text-sm leading-6 text-zinc-600">
                      {repo.description}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-zinc-500">
                    {countLabel(repo.paper_count, "paper")} · Created{" "}
                    {formatDate(repo.created_at)}
                  </p>
                  {datasets.length > 0 && (
                    <ul className="mt-2 space-y-1 text-sm text-zinc-600">
                      {datasets.map((dataset) => (
                        <li key={dataset.id}>
                          <Link
                            to={datasetHref(dataset.id)}
                            className={LINK}
                          >
                            Dataset: {dataset.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
